<template>
  <div
    v-editable="blok"
    class="AccordionBlock layout-container"
    :class="[desktopWidth(blok.textAlign === '2col' ?'lg':'sm'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <a id="AccordionBlock" />
    <div
      :class="{
        'desk:flex w-full justify-between gap-48': blok.textAlign === '2col'
      }"
    >
      <div
        v-if="blok.title || blok.buttonText"
        class="flex flex-row flex-wrap  gap-8 mb-16"
        :class="{
          'flex-col text-center': blok.textAlign === 'center',
          'basis-1/2 flex-col items-start justify-start': blok.textAlign === '2col',
          'justify-between items-center': blok.textAlign !== '2col',
        }"
      >
        <component :is="blok.headerTag ?? 'h2'" class="type-headline-xl mb-8">{{ blok.title }}</component>
        <div
          v-if="blok.content"
          class="wysiwyg mb-12"
          v-html="renderRichText(blok.content)"
        />

        <NuxtLink :to="$sbLinkFix(blok.buttonLink)" :target="$sbLinkTarget(blok.buttonLink)">
          <div
            v-if="blok.buttonText"
            class="btn btn--primary"
            :class="{
              'btn--secondary': blok.buttonStyle === 'secondary',
              'btn--underline': blok.buttonStyle === 'underline',
            }"
          >
            {{ blok.buttonText }}
          </div>
        </NuxtLink>
      </div>
      <div
        :class="{
          'basis-1/2': blok.textAlign === '2col'
        }"
      >
        <div
          v-for="item in blok.itemList"
          :key="item._uid"
          class="py-16 desk:py-20 border-b border-border-base"
        >
          <div
            class="select-none cursor-pointer flex justify-between items-center type-headline-base"
            @click="toggleItem(item._uid)"
          >
            {{ item.label }}
            <img
              :src="openAccordionItems.includes(item._uid) ? '/icons/angle-up.svg' : '/icons/angle-down.svg'"
              class="w-20 h-20 desk:w-24 desk:h-24"
            >
          </div>
          <transition
            name="accordion"
            @enter="enter"
            @leave="leave"
          >
            <div
              v-show="openAccordionItems.includes(item._uid)"
              class="overflow-hidden"
            >
              <div class="pt-8 wysiwyg text-text-subtle">
                <ExtendedWysiwyg :content="item.content" />
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { AccordionBlokType } from '#constants/types/storyblok';
import useVisualAppearancePlugin from '~/composables/useVisualAppearancePlugin';

const props = defineProps<{
  blok: AccordionBlokType,
}>();

const openAccordionItems = ref<string[]>([]);

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const toggleItem = (id: string) => {
  if (openAccordionItems.value.includes(id)) {
    openAccordionItems.value = openAccordionItems.value.filter(item => item !== id);
  } else {
    openAccordionItems.value.push(id);
  }
};

const enter = (el) => {
  el.style.maxHeight = el.scrollHeight + 'px';
};

const leave = (el) => {
  el.style.maxHeight = el.scrollHeight + 'px';
  setTimeout(() => {
    el.style.maxHeight = 0;
  });
};

const stripHtml = (html: string) => {
  return html.replace(/<[^>]*>/g, '').trim();
};

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: props.blok.itemList?.map(item => ({
    '@type': 'Question',
    name: item.label,
    acceptedAnswer: {
      '@type': 'Answer',
      text: stripHtml(renderRichText(item.content))
    }
  })) || []
}));

</script>

<style scoped>
.accordion-enter-active, .accordion-leave-active {
  transition: max-height 0.3s ease;
}
.accordion-enter-from, .accordion-leave-to {
  max-height: 0;
}
</style>
